import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAI } from "../hooks/useAI";
import { useAuth } from "../AuthContext";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import EvidenceList from "../components/dispute/EvidenceList";
import { countryName } from "../data/countries";
import { COLORS, SERIF, SANS, formStyles, pageStyles } from "../components/dispute/theme";
import {
  makeEmptyEvidenceRow,
  filterValidEvidence,
  validateEvidenceCap,
  validateEvidenceInfoRequired,
  evidenceLimitsFor,
} from "../components/dispute/evidence";

export default function DisputeResponse() {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Identify the responder by their logged-in email (these pages require login),
  // falling back to the ?as=<email> tag carried from a fresh invitation link.
  const identityEmail = (
    user?.email || searchParams.get("as") || ""
  ).trim().toLowerCase();
  const navigate = useNavigate();
  const { fetchSharedDispute, addDisputeTurn } = useAI();

  const [dispute, setDispute] = useState(null);
  const [authorRole, setAuthorRole] = useState("disputant");
  const [authorEmail, setAuthorEmail] = useState("");
  const [prompt, setPrompt] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [evidence, setEvidence] = useState([makeEmptyEvidenceRow()]);
  const [submitting, setSubmitting] = useState(false);
  // Once we identify the responder, lock the prefilled fields so they can't be
  // changed (role is locked only when known from the participant record).
  const [emailLocked, setEmailLocked] = useState(false);
  const [roleLocked, setRoleLocked] = useState(false);

  const loadThread = useCallback(async () => {
    const data = await fetchSharedDispute(uuid);
    if (data.error) {
      if (data.status === 401) {
        navigate("/login");
      } else if (data.status === 403) {
        alert("You are not a party to this dispute.");
        navigate("/home");
      } else {
        alert("Dispute not found");
        navigate("/home");
      }
      return;
    }
    setDispute(data);
  }, [uuid, fetchSharedDispute, navigate]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Recognize the responder by their email (logged-in user, or the invite tag)
  // and prefill their email + role from the dispute's participant list. Works
  // for older invites too, since it keys off the logged-in account.
  useEffect(() => {
    if (!dispute || !identityEmail) return;
    const match = (dispute.participants || []).find(
      (p) => (p.email || "").trim().toLowerCase() === identityEmail
    );
    if (match) {
      setAuthorEmail(match.email);
      setEmailLocked(true);
      if (match.role === "disputant" || match.role === "witness") {
        setAuthorRole(match.role);
        setRoleLocked(true);
      }
    } else {
      // Email known but no participant match — prefill (and lock) the email only.
      setAuthorEmail(identityEmail);
      setEmailLocked(true);
    }
  }, [dispute, identityEmail]);

  const canSubmit = !submitting && dispute && prompt.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const validEvidence = filterValidEvidence(evidence);
    const overMsg = validateEvidenceCap(validEvidence, evidenceLimitsFor(authorRole));
    if (overMsg) {
      alert(overMsg);
      return;
    }
    const infoMsg = validateEvidenceInfoRequired(validEvidence);
    if (infoMsg) {
      alert(infoMsg);
      return;
    }
    setSubmitting(true);
    try {
      const result = await addDisputeTurn(uuid, {
        prompt,
        desiredOutcome: desiredOutcome.trim() || null,
        authorRole,
        authorEmail: authorEmail.trim() || null,
        evidence: validEvidence,
      });
      if (result.error) {
        alert(result.error);
        return;
      }
      // A responding disputant with an unmatched stake continues to the
      // stake-matching + payment step; the judgement begins once it's paid.
      if (authorRole === "disputant" && dispute.stake_amount && !dispute.stake_matched_at) {
        navigate(`/match-stake/${uuid}`);
      } else {
        navigate(`/dispute/${uuid}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!dispute) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Loading dispute…</div>
      </div>
    );
  }

  const jurisdictionLine = [countryName(dispute.jurisdiction_state), dispute.jurisdiction_region]
    .filter(Boolean)
    .join(" · ");
  const verdictDate = dispute.judge_at ? new Date(dispute.judge_at).toLocaleString() : null;

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={styles.stack}>
          {/* ── Header card — matches the white + blue-accent-border style
              used on SubmitDispute/DisputeView (was solid navy). ── */}
          <div style={styles.headerCard}>
            <div style={styles.headerCardRow}>
              <h1 style={{ ...pageStyles.pageTitle, color: "#1A5EC4", margin: 0 }}>Add your response</h1>
              <Link to={`/dispute-evidence/${uuid}`} style={styles.viewEvidenceBtn}>
                🗂 View Evidence
              </Link>
            </div>
            <p style={{ ...pageStyles.pageSubtitle, color: COLORS.inkSoft, margin: "8px 0 0 0" }}>
              Add your perspective and evidence to the dispute. The AI Neutrals will weigh it alongside
              the original information. A verdict will be delivered once your perspective has been
              submitted along with any witness information (if there are witnesses).
            </p>
          </div>

          {/* ── Section 1: read-only dispute context ── */}
          <div style={styles.sectionCard}>
            <div style={styles.contextEyebrow}>You're responding to</div>
            <div style={styles.contextSubject}>{dispute.subject || "(no subject)"}</div>
            <div style={styles.contextMeta}>
              {dispute.category_name && <span style={styles.metaPill}>{dispute.category_name}</span>}
              {jurisdictionLine && <span style={styles.metaPill}>{jurisdictionLine}</span>}
              {(dispute.creator_name || dispute.creator_email) && (
                <span>
                  Started by <strong>{dispute.creator_name || dispute.creator_email}</strong>
                </span>
              )}
            </div>
            <Link to={`/dispute/${uuid}`} style={styles.viewThreadLink}>
              ← View the full thread
            </Link>
          </div>

          {/* ── Section 2: who is responding ── */}
          <div style={styles.sectionCard}>
            <div style={formStyles.fieldGroup}>
              <h2 style={formStyles.sectionTitle}>Who are you?</h2>
              <div style={formStyles.rowGroup}>
                <div style={formStyles.rowCol}>
                  <label style={formStyles.fieldLabel}>Your role</label>
                  <select
                    style={{ ...formStyles.select, ...(roleLocked ? styles.lockedField : {}) }}
                    value={authorRole}
                    onChange={(e) => setAuthorRole(e.target.value)}
                    disabled={roleLocked}
                  >
                    <option value="disputant">Disputant (the other party)</option>
                    <option value="witness">Witness</option>
                  </select>
                </div>
                <div style={formStyles.rowCol}>
                  <label style={formStyles.fieldLabel}>Your email (optional)</label>
                  <input
                    type="email"
                    style={{ ...formStyles.textInput, ...(emailLocked ? styles.lockedField : {}) }}
                    placeholder="you@example.com"
                    value={authorEmail}
                    onChange={(e) => setAuthorEmail(e.target.value)}
                    disabled={emailLocked}
                    readOnly={emailLocked}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 3: testimony + desired outcome ── */}
          <div style={styles.sectionCard}>
            <div style={formStyles.fieldGroup}>
              <h2 style={formStyles.sectionTitle}>Your Perspective</h2>
              <textarea
                style={formStyles.textarea}
                placeholder="Explain your side of the dispute and respond to the prior testimony."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
              />
            </div>
            <div style={{ ...formStyles.fieldGroup, marginBottom: 0 }}>
              <h2 style={formStyles.sectionTitle}>Desired outcome (optional)</h2>
              <textarea
                style={formStyles.textarea}
                placeholder="What resolution are you looking for?"
                value={desiredOutcome}
                onChange={(e) => setDesiredOutcome(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* ── Section 4: evidence ── */}
          <div style={styles.sectionCard}>
            <EvidenceList
              evidence={evidence}
              setEvidence={setEvidence}
              title="Evidence"
              maxPerType={evidenceLimitsFor(authorRole)}
            />
          </div>

          {/* ── Section 5: submit ── */}
          <div style={styles.sectionCard}>
            <button
              type="button"
              style={{
                ...formStyles.primaryButton,
                ...(canSubmit ? { background: "#1A5EC4", color: "#fff" } : formStyles.primaryButtonDisabled),
              }}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? "Submitting…" : "Add to dispute"}
            </button>
            {!canSubmit && !submitting && (
              <p style={styles.disabledHint}>Add your testimony to continue.</p>
            )}
            <p style={styles.note}>
              Your response will be added to the dispute. A decision will be announced once all
              information is complete or at the end of seven days — whichever comes first.
            </p>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  loading: {
    display: "flex",
    height: "60vh",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  stack: { width: "100%", maxWidth: 760, display: "flex", flexDirection: "column", gap: 20 },
  headerCard: {
    width: "100%",
    background: "#fff",
    color: COLORS.ink,
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.10)",
    border: "2px solid rgba(26,94,196,0.2)",
  },
  headerCardRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  viewEvidenceBtn: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    background: "transparent",
    color: "#1A5EC4",
    border: "1.5px solid rgba(26,94,196,0.35)",
    borderRadius: 8,
    fontFamily: SANS,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  sectionCard: {
    width: "100%",
    background: COLORS.card,
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  contextEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: COLORS.inkMuted,
    marginBottom: 6,
  },
  contextSubject: {
    fontFamily: SERIF,
    fontSize: 20,
    fontWeight: 600,
    color: COLORS.ink,
    margin: 0,
    lineHeight: 1.3,
  },
  contextMeta: {
    marginTop: 10,
    fontSize: 13,
    color: COLORS.inkMuted,
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  metaPill: {
    background: COLORS.bgAlt,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: COLORS.inkSoft,
  },
  viewThreadLink: {
    display: "inline-block",
    marginTop: 12,
    fontSize: 12,
    color: COLORS.inkSoft,
    textDecoration: "none",
    borderBottom: `1px dashed ${COLORS.borderStrong}`,
  },
  lockedField: { background: COLORS.bgAlt, color: COLORS.inkSoft, cursor: "not-allowed" },
  disabledHint: { color: COLORS.inkMuted, fontSize: 12, margin: "10px 0 0 0", textAlign: "center" },
  note: { color: COLORS.inkSoft, fontSize: 12, margin: "12px 0 0 0", lineHeight: 1.5, textAlign: "center" },
};
